import { Icon } from '@iconify/react';
import { Badge } from '@/components/shadcn/badge';
import { Button } from '@/components/shadcn/button';
import { Skeleton } from '@/components/shadcn/skeleton';
import { cn } from '@/lib/utils';
import type { WhatsappCommunitySession } from '@/services/whatsappCommunityService';
import {
  canLogoutWhatsappSession,
  formatWhatsappDatetime,
  formatWhatsappPhone,
  formatWhatsappSessionStatus,
  whatsappSessionStatusIcon,
  whatsappStatusBadgeClass,
} from '@/components/community/whatsapp/whatsappUiUtils';

interface WhatsappSessionListProps {
  sessions: WhatsappCommunitySession[];
  isLoading: boolean;
  onLogout: (session: WhatsappCommunitySession) => void;
}

function SessionStatusBadge({ status }: { status: string }) {
  return (
    <Badge
      variant="secondary"
      className={cn(
        'inline-flex items-center gap-1 border px-2.5 py-0.5 font-medium',
        whatsappStatusBadgeClass(status)
      )}
    >
      <Icon icon={whatsappSessionStatusIcon(status)} className="h-3.5 w-3.5" />
      {formatWhatsappSessionStatus(status)}
    </Badge>
  );
}

function SessionActions({
  session,
  onLogout,
  className,
}: {
  session: WhatsappCommunitySession;
  onLogout: (session: WhatsappCommunitySession) => void;
  className?: string;
}) {
  if (!canLogoutWhatsappSession(session.status)) {
    return (
      <span className={cn('text-xs text-slate-400', className)}>-</span>
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={cn(
        'h-8 rounded-lg border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700',
        className
      )}
      onClick={() => onLogout(session)}
    >
      <Icon icon="mdi:logout" className="h-4 w-4" />
      Logout
    </Button>
  );
}

const WhatsappSessionList = ({ sessions, isLoading, onLogout }: WhatsappSessionListProps) => {
  if (isLoading && sessions.length === 0) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <Skeleton key={i} className="h-28 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-center">
        <Icon icon="mdi:cellphone-off" className="mx-auto mb-2 h-8 w-8 text-gray-300" />
        <p className="text-sm font-medium text-gray-700">Belum ada session</p>
        <p className="mt-1 text-xs text-gray-500">
          Hubungkan WhatsApp untuk menampilkan nomor di sini.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Mobile cards */}
      <div className="space-y-3 lg:hidden">
        {sessions.map((session) => {
          const inactive = !canLogoutWhatsappSession(session.status);
          return (
            <div
              key={session.session_id}
              className={cn(
                'rounded-xl border bg-white p-3.5 shadow-sm',
                inactive ? 'border-slate-100 opacity-90' : 'border-emerald-100'
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <p className="truncate text-sm font-semibold text-[#0E1726]">
                    {session.device_name || 'Perangkat'}
                  </p>
                  <p className="flex items-center gap-1.5 text-sm text-[#556575]">
                    <Icon icon="mdi:whatsapp" className="h-4 w-4 shrink-0 text-emerald-600" />
                    <span className="truncate">{formatWhatsappPhone(session.phone_number)}</span>
                  </p>
                </div>
                <SessionStatusBadge status={session.status} />
              </div>
              <div className="mt-3 flex items-center justify-between gap-3 border-t border-dashed border-gray-100 pt-3">
                <p className="text-xs text-gray-400">
                  Terakhir aktif
                  <span className="mt-0.5 block font-medium text-gray-600">
                    {formatWhatsappDatetime(session.last_seen)}
                  </span>
                </p>
                <SessionActions session={session} onLogout={onLogout} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop table */}
      <div className="hidden overflow-x-auto rounded-xl border border-gray-100 lg:block">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3">Nama perangkat</th>
              <th className="px-4 py-3">Nomor</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Terakhir aktif</th>
              <th className="px-4 py-3 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sessions.map((session) => {
              const inactive = !canLogoutWhatsappSession(session.status);
              return (
                <tr
                  key={session.session_id}
                  className={cn('bg-white', inactive && 'bg-slate-50/70')}
                >
                  <td className="px-4 py-3.5 font-medium text-[#0E1726]">
                    {session.device_name || '-'}
                  </td>
                  <td className="px-4 py-3.5 text-[#556575]">
                    {formatWhatsappPhone(session.phone_number)}
                  </td>
                  <td className="px-4 py-3.5">
                    <SessionStatusBadge status={session.status} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-3.5 text-gray-500">
                    {formatWhatsappDatetime(session.last_seen)}
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <SessionActions session={session} onLogout={onLogout} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default WhatsappSessionList;
